import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  Checkbox,
  Chip,
  Typography,
  ListItemText,
} from "@mui/material";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";

// pure.white inverts to black in dark mode in this theme, so use a fixed,
// slightly softened white for badge text that always sits on a blue background.
const BADGE_TEXT_WHITE = "rgba(255, 255, 255, 0.88)";

// Inline dropdown for the Area(s) column. Lists the establishment's areas as a
// checkbox menu; checking adds the format to that area, unchecking removes it.
// Each toggle is persisted immediately by the parent via onToggleArea.
const AreaSelectColumn = ({ areas, selectedAreas, onToggleArea, disabled }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const selectedIds = new Set((selectedAreas || []).map((a) => a.id));

  const handleOpen = (event) => {
    if (disabled) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasSelection = selectedAreas && selectedAreas.length > 0;
  const remainingCount = hasSelection ? selectedAreas.length - 1 : 0;

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "default" : "pointer",
          opacity: disabled ? 0.5 : 1,
          paddingLeft: 2,
          paddingRight: 1,
          width: "100%",
        }}
      >
        {hasSelection ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              overflow: "hidden",
            }}
          >
            <Chip
              size="small"
              label={selectedAreas[0].name}
              sx={{
                maxWidth: "110px",
                fontWeight: 600,
                borderRadius: "6px",
                backgroundColor: (theme) => theme.palette.info[600],
                color: BADGE_TEXT_WHITE,
                "& .MuiChip-label": { color: BADGE_TEXT_WHITE },
              }}
            />
            {remainingCount > 0 ? (
              <Chip
                size="small"
                label={`+${remainingCount}`}
                sx={{
                  fontWeight: 700,
                  borderRadius: "6px",
                  backgroundColor: (theme) => theme.palette.info[800],
                  color: BADGE_TEXT_WHITE,
                  "& .MuiChip-label": { color: BADGE_TEXT_WHITE },
                }}
              />
            ) : null}
          </Box>
        ) : (
          <Typography
            variant="body1"
            noWrap
            sx={{ color: (theme) => theme.palette.terrain[400] }}
          >
            Add to Area(s)
          </Typography>
        )}
        <KeyboardArrowDownOutlinedIcon
          sx={{ fontSize: 18, color: (theme) => theme.palette.primary[800] }}
        />
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {areas.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body1">No areas found</Typography>
          </MenuItem>
        ) : (
          areas.map((area) => {
            const checked = selectedIds.has(area.id);
            return (
              <MenuItem
                key={area.id}
                dense
                disabled={disabled}
                onClick={() => onToggleArea(area, !checked)}
              >
                <Checkbox
                  edge="start"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  sx={{ color: (theme) => theme.palette.primary[800] }}
                />
                <ListItemText primary={area.name} />
              </MenuItem>
            );
          })
        )}
      </Menu>
    </>
  );
};

export default AreaSelectColumn;
